/**
 * MeetingNotesDetail - Renders a single meeting notes summary (source, summary,
 * action items, key topics, and an expandable full transcript).
 */

import { useQuery } from '@tanstack/react-query';
import { RefreshCw01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

import { Button } from '@/lib/base/buttons/button';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import {
  MEETING_NOTES_PROVIDER_PATHS,
  MEETING_NOTES_SOURCE_LABELS,
  MeetingNoteAttribution,
} from '@/lib/crm/utils/meetingNotes';
import { apiGet } from '@/lib/http/client';
import {
  createEntityMarkdownComponents,
  entitySanitizeSchema,
  preprocessEntityTags,
} from '@/lib/markdown/entityRendering';
import { useOrgTimeFormat } from '@/lib/time';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { CollapsibleSection } from '../sections/collapsible-section';
import { formatUpcomingMeetingDateTime } from '../sections/upcoming-meetings-utils';
import {
  enrichWithSpeakerMap,
  parseSpeakerMap,
} from '../utils/meetingEnrichment';
import { TranscriptViewer } from './TranscriptViewer';

interface TranscriptResponse {
  transcript_content: string;
  entity_id: string;
  content_type?: string;
}

interface MeetingNotesDetailProps {
  note: CrmObject;
  orgId: number;
  /** ISO 8601 start time of the parent meeting; preferred over the note's created_at for the header timestamp. */
  meetingStartTime?: string;
  /** Entity id of the user who owns this note, for the attribution badge. */
  ownerEntityId?: string;
}

/** Maps notes_source values to their icon paths */
const SOURCE_ICONS: Record<string, string> = {
  google_meet: '/assets/figma/google-meet-icon.svg',
  granola: '/assets/figma/granola-icon.svg',
  zoom: '/assets/figma/zoom-icon.svg',
  fathom: '/assets/figma/fathom-icon.svg',
};

function getTranscriptUrl(
  orgId: number,
  entityId: string,
  source: string | undefined
): string {
  const pathSegment =
    (source && MEETING_NOTES_PROVIDER_PATHS[source]) || 'meetings';
  return `/api/orgs/${orgId}/${pathSegment}/notes/${entityId}/transcript/`;
}

function formatSourceLabel(source: string | undefined): string {
  return source ? (MEETING_NOTES_SOURCE_LABELS[source] ?? source) : 'Unknown';
}

/**
 * Individual meeting notes detail component.
 */
export function MeetingNotesDetail({
  note,
  orgId,
  meetingStartTime,
  ownerEntityId,
}: MeetingNotesDetailProps) {
  const { timeFormat } = useOrgTimeFormat();

  const title =
    getPropertyValue<string>(note.properties?.ahoy_title) || 'Meeting Summary';
  const source = getPropertyValue<string>(note.properties?.ahoy_notes_source);
  const summary = getPropertyValue<string>(note.properties?.ahoy_summary);
  const actionItems = getPropertyValue<string>(
    note.properties?.ahoy_action_items
  );
  const keyTopics = getPropertyValue<string>(note.properties?.ahoy_key_topics);
  const createdAt = getPropertyValue<string>(note.properties?.ahoy_created_at);
  const displayTimestamp = meetingStartTime || createdAt;
  const isCreatedAtFallback = !meetingStartTime && Boolean(createdAt);
  const formattedTimestamp =
    displayTimestamp && !Number.isNaN(new Date(displayTimestamp).getTime())
      ? formatUpcomingMeetingDateTime(displayTimestamp, timeFormat)
      : 'Unknown date';
  const speakerMapRaw = getPropertyValue<string>(
    note.properties?.ahoy_speaker_map
  );
  const speakerMap = parseSpeakerMap(speakerMapRaw);
  const transcriptFileId = getPropertyValue<string>(
    note.properties?.ahoy_transcript_file_id
  );

  // Track whether the transcript section is expanded
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

  // Fetch transcript content only when the section is expanded
  const {
    data: transcriptData,
    isLoading: isLoadingTranscript,
    error: transcriptError,
    refetch: refetchTranscript,
  } = useQuery({
    queryKey: ['meetingTranscript', orgId, note.entity_id, source],
    queryFn: async () => {
      const response = await apiGet<TranscriptResponse>(
        getTranscriptUrl(orgId, note.entity_id, source)
      );
      return response;
    },
    retry: 1,
    staleTime: Infinity, // Transcripts are immutable once created
    enabled: isTranscriptOpen, // Only fetch when transcript section is expanded
  });

  const handleRefetchTranscript = useCallback(() => {
    void refetchTranscript();
  }, [refetchTranscript]);

  return (
    <div className='p-4 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]'>
      {/* Header */}
      <div className='mb-3'>
        <h3 className='text-sm font-semibold text-fg-primary'>{title}</h3>
        <div className='flex items-center gap-2 mt-1'>
          {source && (
            <span className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]'>
              {SOURCE_ICONS[source] && (
                <img
                  src={SOURCE_ICONS[source]}
                  alt={formatSourceLabel(source)}
                  className='size-3.5 object-contain'
                />
              )}
              {formatSourceLabel(source)}
            </span>
          )}
          <span className='text-xs text-fg-tertiary'>
            {isCreatedAtFallback
              ? `Created ${formattedTimestamp}`
              : formattedTimestamp}
          </span>
          <MeetingNoteAttribution
            ownerEntityId={ownerEntityId}
            orgId={orgId}
            interactive={true}
            variant='tab'
            alwaysShowSeparator={true}
          />
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className='mb-3'>
          <h4 className='text-xs font-medium text-fg-quaternary mb-1'>
            Summary
          </h4>
          <MarkdownContent
            content={preprocessEntityTags(
              enrichWithSpeakerMap(summary, speakerMap)
            )}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, entitySanitizeSchema]]}
            components={createEntityMarkdownComponents(orgId)}
            size='compact'
            className='text-xs'
          />
        </div>
      )}

      {/* Action Items */}
      {actionItems && (
        <div className='mb-3'>
          <h4 className='text-xs font-medium text-fg-quaternary mb-1'>
            Action items
          </h4>
          <MarkdownContent
            content={preprocessEntityTags(
              enrichWithSpeakerMap(actionItems, speakerMap)
            )}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, entitySanitizeSchema]]}
            components={createEntityMarkdownComponents(orgId)}
            size='compact'
            className='text-xs'
          />
        </div>
      )}

      {/* Key Topics */}
      {keyTopics && (
        <div className='mb-3'>
          <h4 className='text-xs font-medium text-fg-quaternary mb-1'>
            Key topics
          </h4>
          <MarkdownContent
            content={preprocessEntityTags(
              enrichWithSpeakerMap(keyTopics, speakerMap)
            )}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, entitySanitizeSchema]]}
            components={createEntityMarkdownComponents(orgId)}
            size='compact'
            className='text-xs'
          />
        </div>
      )}

      {/* Transcript — only show when a transcript file exists */}
      {transcriptFileId && (
        <CollapsibleSection
          title='Full transcript'
          defaultExpanded={false}
          onExpandedChange={setIsTranscriptOpen}
        >
          {isLoadingTranscript && (
            <div className='text-xs text-fg-tertiary'>Loading transcript…</div>
          )}
          {transcriptError && (
            <div className='space-y-2'>
              <p className='text-xs text-[var(--color-text-error-primary)]'>
                We couldn&apos;t load the transcript
              </p>
              <Button
                color='secondary'
                size='sm'
                onClick={handleRefetchTranscript}
                iconLeading={RefreshCw01}
              >
                Retry
              </Button>
            </div>
          )}
          {transcriptData && (
            <TranscriptViewer
              content={transcriptData.transcript_content}
              contentType={transcriptData.content_type || 'text/plain'}
              orgId={orgId}
            />
          )}
        </CollapsibleSection>
      )}
    </div>
  );
}
